/**
 * Build A Flexi — storefront page logic
 *
 * Flow:
 *  1. Page load → GET /webhook/build-a-flexi-check → baseline order ID stored
 *  2. Auto-polling starts immediately (every 10s, restarts indefinitely)
 *  3. Customer designs in CFM and clicks "Create Order" inside CFM
 *     (CFM POSTs the order to its own API; n8n detects it via the check endpoint)
 *  4. Poll detects new order ID → POST /webhook/build-a-flexi → variant_id OR error
 *  5a. Happy path: POST /cart/add.js → success state → resume polling for next add
 *  5b. color_limit / no_sku / custom_size: show out-of-scope UI
 *  5c. Fetch error: show error + "Try Again" button → retry processOrder with same ID
 */

(function () {
  'use strict';

  var CFG = {
    checkUrl:       'https://n8n.exl3d.com/webhook/build-a-flexi-check',
    processUrl:     'https://n8n.exl3d.com/webhook/build-a-flexi',
    customOrderUrl: 'https://n8n.exl3d.com/webhook/build-a-flexi-custom',
    pollInterval:   10000  // ms between polls
  };

  var baselineOrderId = null;
  var pollTimer       = null;
  var processing      = false;  // true while processOrder is in-flight
  var lastOrderId            = null;
  var lastShareCode          = null;
  var lastPreviewUrl         = null;
  var lastShareUrl           = null;
  var lastCreatedAt          = null;
  var lastQuantity           = null;
  var lastCustomerEmail      = null;
  var lastCustomerFirstName  = null;
  var lastCustomerLastName   = null;
  var lastCustomerPhone      = null;
  var lastCustomerAddress    = null;
  var lastCustomerNote       = null;
  var lastColorCount         = null;
  var lastSegments           = null;
  var lastScale              = null;

  // ── DOM refs (set after DOMContentLoaded) ─────────────────────────────────
  var retryBtn, statusWrap, statusMsg, errorMsg, successWrap, outOfScopeWrap,
      outOfScopeMsg, oosActions, btnBackToEdit, btnCustomOrder;

  // ── UI helpers ─────────────────────────────────────────────────────────────
  function setStatus(msg, showSpinner) {
    statusWrap.style.display     = 'flex';
    statusMsg.textContent        = msg;
    errorMsg.style.display       = 'none';
    successWrap.style.display    = 'none';
    outOfScopeWrap.style.display = 'none';
    if (retryBtn)    retryBtn.style.display    = 'none';
    if (btnBackToEdit) btnBackToEdit.style.display = 'none';
    var spinner = document.querySelector('.baf-spinner');
    if (spinner) spinner.style.display = showSpinner ? '' : 'none';
  }

  function setError(msg) {
    stopPolling();
    statusWrap.style.display     = 'none';
    successWrap.style.display    = 'none';
    outOfScopeWrap.style.display = 'none';
    errorMsg.textContent         = msg;
    errorMsg.style.display       = 'block';
    processing                   = false;
    if (retryBtn) {
      retryBtn.style.display = '';
      retryBtn.disabled      = false;
    }
    if (btnBackToEdit) btnBackToEdit.style.display = '';
  }

  function setOutOfScope(msg) {
    stopPolling();
    statusWrap.style.display     = 'none';
    errorMsg.style.display       = 'none';
    successWrap.style.display    = 'none';
    outOfScopeMsg.textContent    = msg;
    outOfScopeWrap.style.display = 'block';
    processing                   = false;
    if (retryBtn)    retryBtn.style.display    = 'none';
    if (btnBackToEdit) btnBackToEdit.style.display = '';
  }

  function setSuccess() {
    statusWrap.style.display     = 'none';
    errorMsg.style.display       = 'none';
    outOfScopeWrap.style.display = 'none';
    successWrap.style.display    = 'block';
    processing                   = false;
    if (retryBtn)    retryBtn.style.display    = 'none';
    if (btnBackToEdit) btnBackToEdit.style.display = 'none';
    // Update baseline so the next poll won't re-process the same order
    baselineOrderId = lastOrderId;
    // Hold success message for 6s before resuming the idle polling state
    setTimeout(function () {
      successWrap.style.display = 'none';
      startPolling();
    }, 10000);
  }

  function clearOutOfScope() {
    outOfScopeWrap.style.display = 'none';
    errorMsg.style.display       = 'none';
    if (retryBtn)    retryBtn.style.display    = 'none';
    if (btnBackToEdit) btnBackToEdit.style.display = 'none';
    if (oosActions) oosActions.style.display = '';
    var confirmed = document.getElementById('baf-custom-confirmed');
    if (confirmed) confirmed.style.display = 'none';
    if (btnCustomOrder) {
      btnCustomOrder.disabled    = false;
      btnCustomOrder.textContent = btnCustomOrder.dataset.label || 'Request Custom Order';
    }
    // Resume polling
    startPolling();
  }

  // ── Network helpers ────────────────────────────────────────────────────────
  function apiFetch(url, opts) {
    return fetch(url, Object.assign({ cache: 'no-store' }, opts))
      .then(function (r) {
        if (!r.ok) {
          return r.text().then(function (body) {
            throw new Error('HTTP ' + r.status + ': ' + body);
          });
        }
        return r.json();
      });
  }

  // ── Cart event ─────────────────────────────────────────────────────────────
  function notifyCart() {
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  // ── Polling ────────────────────────────────────────────────────────────────
  function stopPolling() {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  function startPolling() {
    stopPolling();
    setStatus('Design your flexi and click "Create Order" — we\'ll detect it automatically.', false);
    pollTimer = setInterval(function () {
      if (processing) return; // order in-flight, skip this tick
      apiFetch(CFG.checkUrl + '?since_id=' + (baselineOrderId || 0))
        .then(function (data) {
          if (data.newOrderId && data.newOrderId !== baselineOrderId) {
            lastShareCode         = data.share_code || null;
            lastPreviewUrl        = data.preview_url || null;
            lastShareUrl          = data.share_url || null;
            lastCreatedAt         = data.created_at || null;
            lastQuantity          = data.quantity || null;
            lastCustomerEmail     = data.customer_email || null;
            lastCustomerFirstName = data.customer_first_name || null;
            lastCustomerLastName  = data.customer_last_name || null;
            lastCustomerPhone     = data.customer_phone_number || null;
            lastCustomerAddress   = data.customer_shipping_address || null;
            lastCustomerNote      = data.customer_note || null;
            lastColorCount        = data.color_count != null ? data.color_count : null;
            lastSegments          = data.segments != null ? data.segments : null;
            lastScale             = data.scale != null ? data.scale : null;
            processOrder(data.newOrderId);
          }
        })
        .catch(function (e) {
          console.warn('[BAF] Poll error:', e.message);
          // Silent — keep polling, don't surface transient network errors
        });
    }, CFG.pollInterval);
  }

  // ── Core logic ─────────────────────────────────────────────────────────────
  function init() {
    setStatus('Connecting…', true);
    apiFetch(CFG.checkUrl)
      .then(function (data) {
        baselineOrderId = data.latestOrderId;
        console.log('[BAF] Baseline order ID:', baselineOrderId);
        startPolling();
      })
      .catch(function (e) {
        console.warn('[BAF] Could not fetch baseline:', e.message);
        baselineOrderId = 0;
        startPolling();
      });
  }

  var SIZE_LABELS = { 35: 'Mini (35%)', 50: 'Medium (50%)', 100: 'Large (100%)' };
  var TAIL = ' Please click the \'Back to Editing\' button, edit your design, then click "Create Order" again. Or request a custom quote below.';

  function handleOutOfScope(data) {
    // Advance baseline so this order is never re-processed if the customer goes back to editing
    baselineOrderId = lastOrderId;
    var sizeLabel = SIZE_LABELS[data.scale] || ('Scale ' + data.scale + '%');
    var msg;
    if (data.error === 'color_limit') {
      msg = 'Your design has ' + data.color_count + ' colors — our automated flow supports up to ' + data.limit + '.' + TAIL;
    } else if (data.error === 'no_sku') {
      msg = 'The maximum segments for ' + sizeLabel + ' size is ' + data.max_segments + '. Your current design has ' + data.segments + ' segments and is outside our automated flow.' + TAIL;
    } else if (data.error === 'custom_size') {
      msg = 'Our automated flow supports Mini (35%), Medium (50%), and Large (100%). Your custom scale of ' + data.scale + '% is outside our automated flow.' + TAIL;
    } else {
      msg = 'This design is outside our automated flow.' + TAIL;
    }
    setOutOfScope(msg);
  }

  function processOrder(orderId) {
    processing  = true;
    lastOrderId = orderId;
    setStatus('Order detected — sending to our system…', true);
    apiFetch(CFG.processUrl, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        cfm_order_id:              orderId,
        share_code:                lastShareCode,
        preview_url:               lastPreviewUrl,
        share_url:                 lastShareUrl,
        created_at:                lastCreatedAt,
        quantity:                  lastQuantity,
        customer_email:            lastCustomerEmail,
        customer_first_name:       lastCustomerFirstName,
        customer_last_name:        lastCustomerLastName,
        customer_phone_number:     lastCustomerPhone,
        customer_shipping_address: lastCustomerAddress,
        customer_note:             lastCustomerNote,
        color_count:               lastColorCount,
        segments:                  lastSegments,
        scale:                     lastScale
      })
    })
      .then(function (data) {
        if (data.error) {
          data.cfm_order_id = orderId;
          handleOutOfScope(data);
          return;
        }
        if (!data.variant_id) throw new Error('No variant_id returned');
        setStatus('Got it — adding your flexi to the cart…', true);
        return apiFetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            items: [{
              id:         data.variant_id,
              quantity:   data.quantity || 1,
              properties: data.properties || {}
            }]
          })
        }).then(function () {
          notifyCart();
          setSuccess();
        });
      })
      .catch(function (e) {
        console.error('[BAF] Process error:', e.message);
        setError('Something went wrong: ' + e.message + ' — click Try Again or contact us.');
      });
  }

  function submitCustomOrder() {
    btnCustomOrder.disabled    = true;
    btnCustomOrder.textContent = 'Submitting…';
    apiFetch(CFG.customOrderUrl, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        cfm_order_id:              lastOrderId,
        share_code:                lastShareCode,
        preview_url:               lastPreviewUrl,
        share_url:                 lastShareUrl,
        created_at:                lastCreatedAt,
        quantity:                  lastQuantity,
        customer_email:            lastCustomerEmail,
        customer_first_name:       lastCustomerFirstName,
        customer_last_name:        lastCustomerLastName,
        customer_phone_number:     lastCustomerPhone,
        customer_shipping_address: lastCustomerAddress,
        customer_note:             lastCustomerNote,
        color_count:               lastColorCount,
        segments:                  lastSegments,
        scale:                     lastScale
      })
    })
      .then(function () {
        if (oosActions) oosActions.style.display = 'none';
        var confirmed = document.getElementById('baf-custom-confirmed');
        if (confirmed) confirmed.style.display = 'block';
        if (retryBtn) retryBtn.style.display = 'none';
      })
      .catch(function (e) {
        console.error('[BAF] Custom order error:', e.message);
        btnCustomOrder.disabled    = false;
        btnCustomOrder.textContent = btnCustomOrder.dataset.label || 'Request Custom Order';
        setError('Could not submit your request. Please try again or contact us.');
      });
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    retryBtn         = document.getElementById('baf-btn');
    statusWrap       = document.getElementById('baf-status-wrap');
    statusMsg        = document.getElementById('baf-status-msg');
    errorMsg         = document.getElementById('baf-error');
    successWrap      = document.getElementById('baf-success');
    outOfScopeWrap   = document.getElementById('baf-out-of-scope');
    outOfScopeMsg    = document.getElementById('baf-oos-msg');
    oosActions       = document.getElementById('baf-oos-actions');
    btnBackToEdit    = document.getElementById('baf-btn-back');
    btnCustomOrder   = document.getElementById('baf-btn-custom');

    // Retry: re-calls processOrder with the last detected order ID
    if (retryBtn) retryBtn.addEventListener('click', function () {
      if (lastOrderId) {
        processing = false;
        processOrder(lastOrderId);
      } else {
        startPolling();
      }
    });
    if (btnBackToEdit)  btnBackToEdit.addEventListener('click', clearOutOfScope);
    if (btnCustomOrder) btnCustomOrder.addEventListener('click', submitCustomOrder);

    init();
  });
})();
